import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiClient } from '../services/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import BibliothequeFiltersCard from '../components/jours-feries/BibliothequeFiltersCard';
import BibliothequeTableCard from '../components/jours-feries/BibliothequeTableCard';
import BibliothequeHolidayFormDialog from '../components/jours-feries/BibliothequeHolidayFormDialog';
import BibliothequeImportDialog from '../components/jours-feries/BibliothequeImportDialog';

const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i);
const LIMIT = 50;

const EMPTY_FORM = {
  name: '',
  date: '',
  year: new Date().getFullYear(),
  type: 'NATIONAL',
  country: 'Tunisie',
  is_recurring: false,
  recurrence_pattern: '',
  description: '',
};

const JoursFeriesBibliotheque = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterType, setFilterType] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [page, setPage] = useState(1);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [importFile, setImportFile] = useState(null);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importCountry, setImportCountry] = useState('Tunisie');
  const [autoCreateCalendar, setAutoCreateCalendar] = useState(false);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterYear, filterType, filterCountry, searchTerm]);

  const holidaysQ = useQuery({
    queryKey: ['public-holidays', page, filterYear, filterType, filterCountry, searchTerm],
    queryFn: async () => {
      const params = {
        skip: (page - 1) * LIMIT,
        limit: LIMIT,
        ...(filterYear !== 'all' && { year: parseInt(filterYear) }),
        ...(filterType !== 'all' && { type: filterType }),
        ...(filterCountry !== 'all' && { country: filterCountry }),
        ...(searchTerm && { search: searchTerm }),
      };
      const res = await apiClient.get('v1/holidays/public-holidays', { params });
      return res.data;
    },
  });

  const holidays = holidaysQ.data?.holidays ?? [];
  const totalPages = Math.max(1, Math.ceil((holidaysQ.data?.total ?? 0) / LIMIT));
  const loading = holidaysQ.isPending;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['public-holidays'] });

  const resetForm = () => setFormData(EMPTY_FORM);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, year: parseInt(formData.year) };
      if (editingHoliday) {
        await apiClient.put(`v1/holidays/public-holidays/${editingHoliday.id}`, data);
        toast.success(t('holidayLibrary.toasts.editSuccess'));
      } else {
        await apiClient.post('v1/holidays/public-holidays', data);
        toast.success(t('holidayLibrary.toasts.createSuccess'));
      }
      setShowCreateDialog(false);
      setEditingHoliday(null);
      resetForm();
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('holidayLibrary.toasts.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('holidayLibrary.toasts.deleteConfirm'))) return;
    try {
      await apiClient.delete(`v1/holidays/public-holidays/${id}`);
      toast.success(t('holidayLibrary.toasts.deleteSuccess'));
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('holidayLibrary.toasts.deleteError'));
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      year: holiday.year,
      type: holiday.holiday_type || holiday.type,
      country: holiday.country || '',
      is_recurring: holiday.is_recurring,
      recurrence_pattern: holiday.recurrence_pattern || '',
      description: holiday.description || '',
    });
    setShowCreateDialog(true);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) { toast.error(t('holidayLibrary.toasts.selectFile')); return; }
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const response = await apiClient.post(
        `v1/holidays/public-holidays/import-csv?year=${importYear}&country=${importCountry}&auto_create_calendar=${autoCreateCalendar}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(t('holidayLibrary.toasts.importSuccess', { count: response.data.imported_count }));
      if (response.data.errors?.length > 0) {
      }
      setShowImportDialog(false);
      setImportFile(null);
      invalidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('holidayLibrary.toasts.importError'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('holidayLibrary.title')}</h1>
          <p className="text-muted-foreground">{t('holidayLibrary.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportDialog(true)}>
            <Upload className="mr-2 h-4 w-4" />
            {t('holidayLibrary.importCSV')}
          </Button>
          <Button onClick={() => { resetForm(); setEditingHoliday(null); setShowCreateDialog(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            {t('holidayLibrary.newHoliday')}
          </Button>
        </div>
      </div>

      <BibliothequeFiltersCard
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        filterYear={filterYear} setFilterYear={setFilterYear}
        filterType={filterType} setFilterType={setFilterType}
        filterCountry={filterCountry} setFilterCountry={setFilterCountry}
        years={years}
      />

      <BibliothequeTableCard
        loading={loading}
        holidays={holidays}
        totalPages={totalPages}
        page={page}
        setPage={setPage}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />

      <BibliothequeHolidayFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        editingHoliday={editingHoliday}
        setEditingHoliday={setEditingHoliday}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />

      <BibliothequeImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        importFile={importFile}
        setImportFile={setImportFile}
        importYear={importYear}
        setImportYear={setImportYear}
        importCountry={importCountry}
        setImportCountry={setImportCountry}
        autoCreateCalendar={autoCreateCalendar}
        setAutoCreateCalendar={setAutoCreateCalendar}
        handleImport={handleImport}
      />
    </div>
  );
};

export default JoursFeriesBibliotheque;
